import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../aws-config';
import { Server, ArrowLeft, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { STAGES, getLeaseDuration } from './fleetConstants';
import FleetTabs from './FleetTabs';
import FleetLaneCard from './FleetLaneCard';
import FleetDetailPanel from './FleetDetailPanel';

const API_BASE = awsConfig.API.REST.csvReviewApi.endpoint;

async function fleetApi(path, options = {}) {
  const session = await fetchAuthSession();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
  };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

function FleetManager() {
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLane, setSelectedLane] = useState(null);
  const [selectedLanePerTab, setSelectedLanePerTab] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState('');
  const [, setTick] = useState(0);
  const [expandedStores, setExpandedStores] = useState({});
  const [activeTab, setActiveTab] = useState(STAGES.BETA.key);
  const navigate = useNavigate();
  const releasingRef = useRef(new Set());
  const initializedRef = useRef(false);

  const fetchLanes = useCallback(async () => {
    try {
      const data = await fleetApi('/fleet/lanes');
      const fetchedLanes = data.lanes || [];
      setLanes(fetchedLanes);
      if (!initializedRef.current && fetchedLanes.length > 0) {
        initializedRef.current = true;
        const storeIds = [...new Set(fetchedLanes.map(l => l.storeId))];
        setExpandedStores(Object.fromEntries(storeIds.map(id => [id, true])));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLanes();
    fetchAuthSession().then(s => {
      setCurrentUser(s.tokens?.idToken?.payload?.email?.split('@')[0] || '');
    });
    const interval = setInterval(fetchLanes, 30000);
    return () => clearInterval(interval);
  }, [fetchLanes]);

  // Auto-release expired lanes owned by current user
  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
      lanes.filter(l => l.status === 'IN_USE' && l.allocatedAt &&
        (l.allocatedBy === currentUser || l.allocatedBy === currentUser + '@amazon.com')
      ).forEach(lane => {
        const remaining = getLeaseDuration(lane) - (Date.now() - new Date(lane.allocatedAt).getTime());
        if (remaining <= 0 && !releasingRef.current.has(lane.laneId)) {
          releasingRef.current.add(lane.laneId);
          fleetApi(`/fleet/lanes/${lane.laneId}/release`, { method: 'POST' })
            .then(() => fetchLanes())
            .catch(err => console.warn(`Auto-release failed for ${lane.laneId}:`, err))
            .finally(() => releasingRef.current.delete(lane.laneId));
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lanes, currentUser, fetchLanes]);

  const handleAllocate = async (laneId, purpose, durationSeconds) => {
    setActionLoading(true);
    setError('');
    try {
      await fleetApi(`/fleet/lanes/${laneId}/allocate`, {
        method: 'POST',
        body: JSON.stringify({ purpose, duration: durationSeconds })
      });
      await fetchLanes();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async (laneId) => {
    setActionLoading(true);
    setError('');
    try {
      await fleetApi(`/fleet/lanes/${laneId}/release`, { method: 'POST' });
      await fetchLanes();
      setSelectedLane(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async (laneId, durationSeconds) => {
    setActionLoading(true);
    setError('');
    try {
      await fleetApi(`/fleet/lanes/${laneId}/extend`, { method: 'POST', body: JSON.stringify({ durationSeconds }) });
      await fetchLanes();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle tab switching with per-tab lane memory
  const handleTabSwitch = (targetTab) => {
    if (targetTab === activeTab) return;
    setSelectedLanePerTab(prev => ({ ...prev, [activeTab]: selectedLane }));
    setActiveTab(targetTab);

    const saved = selectedLanePerTab[targetTab];
    const fresh = saved ? lanes.find(l => l.laneId === saved.laneId) : null;
    if (fresh && fresh.status === 'IN_USE') {
      setSelectedLane(fresh);
    } else {
      const myLane = lanes.find(
        l => l.deploymentStage === targetTab &&
             l.status === 'IN_USE' &&
             (l.allocatedBy === currentUser || l.allocatedBy === currentUser + '@amazon.com')
      );
      setSelectedLane(myLane || null);
    }
  };

  // Filter lanes by active tab
  const filteredLanes = useMemo(() =>
    activeTab === 'All' ? lanes : lanes.filter(l => l.deploymentStage === activeTab),
    [lanes, activeTab]
  );

  // Group lanes by store
  const grouped = filteredLanes.reduce((acc, lane) => {
    const key = lane.storeId || 'Unknown';
    if (!acc[key]) acc[key] = { iss: null, lanes: [], label: lane.storeLabel || key, enterpriseUrl: lane.enterpriseUrl };
    if (lane.laneType === 'ISS') acc[key].iss = lane;
    else acc[key].lanes.push(lane);
    return acc;
  }, {});

  const storeOrder = ['10145', '10252', '10071', '10438'];
  const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
    const ai = storeOrder.indexOf(a), bi = storeOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // Update selectedLane from fresh data
  useEffect(() => {
    setSelectedLane(prev => {
      if (!prev) return prev;
      const fresh = lanes.find(l => l.laneId === prev.laneId);
      return fresh || null;
    });
  }, [lanes]);

  const freeLanes = useMemo(() => filteredLanes.filter(l => l.status === 'FREE' && l.laneType !== 'ISS').length, [filteredLanes]);
  const inUseLanes = useMemo(() => filteredLanes.filter(l => l.status === 'IN_USE' && l.laneType !== 'ISS').length, [filteredLanes]);
  const totalLanes = useMemo(() => filteredLanes.filter(l => l.laneType !== 'ISS').length, [filteredLanes]);

  // Memoized tab badge counts
  const tabCounts = useMemo(() => {
    const counts = {};
    Object.values(STAGES).forEach(stage => {
      counts[stage.key] = lanes.filter(l => l.deploymentStage === stage.key && l.laneType !== 'ISS').length;
    });
    return counts;
  }, [lanes]);

  // ISS lane for the selected lane's store
  const storeIss = selectedLane ? lanes.find(l => l.storeId === selectedLane.storeId && l.laneType === 'ISS') : null;

  if (loading) return <div className="loading">Loading fleet data...</div>;

  return (
    <div className="fleet-manager">
      <button className="btn btn-secondary btn-sm back-to-dashboards" onClick={() => navigate('/dashboards')}><ArrowLeft size={14} /> Back to Dashboards</button>
      <div className="fleet-header">
        <div>
          <h2>Fleet Manager</h2>
          <p className="fleet-subtitle">QA Lab Lane Allocation — click a lane to allocate or release</p>
        </div>
        <div className="fleet-summary">
          <span className="fleet-stat fleet-stat-free">{freeLanes} Free</span>
          <span className="fleet-stat fleet-stat-busy">{inUseLanes} In Use</span>
          <span className="fleet-stat fleet-stat-total">{totalLanes} Total</span>
          <button className="btn btn-sm btn-secondary" onClick={fetchLanes} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && <div className="fleet-error">{error} <button onClick={() => setError('')}>×</button></div>}

      <FleetTabs activeTab={activeTab} onTabSwitch={handleTabSwitch} tabCounts={tabCounts} />

      <div className="fleet-content">
        <div className="fleet-grid">
          {sortedGroups.map(([storeId, group]) => (
            <div key={storeId} className="fleet-store-group">
              <div className="fleet-store-header" onClick={() => setExpandedStores(prev => ({ ...prev, [storeId]: !prev[storeId] }))}>
                {expandedStores[storeId] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <Server size={16} />
                <span>{group.label}</span>
                <span className="fleet-store-count">{group.lanes.filter(l => l.status === 'IN_USE').length}/{group.lanes.length} in use</span>
              </div>
              {group.iss && (
                <div className="fleet-store-info">
                  <div className="fleet-info-row">
                    <span className="fleet-info-label">ISS:</span>
                    <a href={group.iss.webUiUrl} target="_blank" rel="noreferrer">{group.iss.ipAddress}:{group.iss.port}</a>
                  </div>
                </div>
              )}
              {expandedStores[storeId] && (
                <>
                  <div className="fleet-lane-cards">
                    {group.lanes.filter(l => l.laneType === 'POS').length > 0 && (
                      <>
                        <div className="fleet-lane-type-label">POS</div>
                        <div className="fleet-lane-row">
                          {group.lanes.filter(l => l.laneType === 'POS').sort((a, b) => a.hardwareId.localeCompare(b.hardwareId)).map(lane => (
                            <FleetLaneCard key={lane.laneId} lane={lane} isSelected={selectedLane?.laneId === lane.laneId} onSelect={setSelectedLane} />
                          ))}
                        </div>
                      </>
                    )}
                    {group.lanes.filter(l => l.laneType === 'SCO').length > 0 && (
                      <>
                        <div className="fleet-lane-type-label">SCO</div>
                        <div className="fleet-lane-row">
                          {group.lanes.filter(l => l.laneType === 'SCO').sort((a, b) => a.hardwareId.localeCompare(b.hardwareId)).map(lane => (
                            <FleetLaneCard key={lane.laneId} lane={lane} isSelected={selectedLane?.laneId === lane.laneId} onSelect={setSelectedLane} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {selectedLane && (
          <FleetDetailPanel
            selectedLane={selectedLane}
            storeIss={storeIss}
            currentUser={currentUser}
            onRelease={handleRelease}
            onAllocate={handleAllocate}
            onExtend={handleExtend}
            onClose={() => setSelectedLane(null)}
            actionLoading={actionLoading}
          />
        )}
      </div>
    </div>
  );
}

export default FleetManager;
